/**
 * "Watch a Session" (owner decision 2026-10-03): any user of the tenant may
 * join any other live call, bot or standalone, as a read-only watcher. Copy and
 * limits for the list, the standalone join and the watcher's own controls.
 */

/** Every REST call behind the list and the join gives up after this long. */
export const WATCH_REQUEST_TIMEOUT_MS = 10_000;

/** The standalone service names a watcher's participant `observer_<n>`. */
export const STANDALONE_OBSERVER_PREFIX = 'observer_';

/**
 * How a standalone join takes part (services C1 `mode`): a read-only observer
 * (the default an old app sends) or a co-seller streaming their own mic as
 * `salesperson_<n>` (D6, 2026-10-05).
 */
export const STANDALONE_JOIN_MODE = {
  observer: 'observer',
  salesperson: 'salesperson',
} as const;

export type StandaloneJoinMode = (typeof STANDALONE_JOIN_MODE)[keyof typeof STANDALONE_JOIN_MODE];

export const WATCH_SESSION_COPY = {
  title: 'Watch a Session',
  // The desktop apps' subtitle (macOS Models/CoSellerSession.swift, 2026-10-05): a mic join is not view-only.
  subtitle: "Watch another salesperson's active session, or join it with your mic if you are on the call.",
  yourMeetings: 'YOUR MEETINGS',
  liveSessions: 'LIVE SESSIONS',
  loading: 'Loading live sessions...',
  empty: 'No live sessions right now.',
  connecting: 'Connecting to session...',
  resume: 'Resume',
  connect: 'Connect',
  joinsAutomatically: 'Joins automatically',
  notReady: 'Not ready',
  refresh: 'Refresh',
  cancel: 'Cancel',
  close: 'Close',
  you: 'You',
  untitled: 'Untitled call',
  unknownOwner: 'Unknown owner',
  modeBot: 'Bot',
  modeStandalone: 'Desktop',
  abandoned: 'Nobody is connected. You can rejoin it.',
  botSessionsFailed: "Couldn't load bot calls. Try Refresh.",
  standaloneSessionsFailed: "Couldn't load desktop calls. Try Refresh.",
  standaloneSessionsForbidden: "You don't have permission to see desktop calls.",
  joinFailed: "Couldn't join that call. It may have just ended.",
  joinForbidden: "You don't have permission to watch that call.",
  // Someone else's call, bot or desktop (owner, 2026-10-07): one action, which opens the choice step.
  join: 'Join',
  joinTitle: (sessionName: string): string => `Join "${sessionName}"`,
} as const;

/**
 * Joining someone's desktop call with your own mic (D6, 2026-10-05): the same
 * states and copy as the macOS and WPF apps (the copy in macOS
 * Models/CoSellerSession.swift, whose NEW lines both desktops share; the rules
 * in workspace docs/LIVE_CALL_PARTICIPATION.md). Only the user's
 * mic is sent; the owner's app captures the customer audio. The choice step
 * (Join as Co-Seller / Join as Observer) and the header label are the
 * owner-approved co-seller join redesign (2026-10-07); a co-seller's mic is on
 * until they leave: there is no mute.
 */
export const COSELLER_MIC_COPY = {
  joinAsCoSeller: 'Join as Co-Seller',
  /** The list says this user already joined that call with a mic (`joined_as`): the same participant id comes back. */
  rejoinAsCoSeller: 'Rejoin as Co-Seller',
  coSellerExplainer:
    'In this meeting too? Join as a co-seller: your microphone joins the call, so what you say is transcribed as you, ' +
    'a salesperson, and never mistaken for the customer. This is what co-seller mode is for.',
  botCallNote: 'The meeting bot already hears everyone in the meeting, so bot calls have no co-seller mode.',
  /** Row 1 of the live session header, in place of the owner's "Live". */
  headerLabel: 'Live · Co-Seller',
  microphone: 'Microphone',
  refreshMicrophones: 'Refresh',
  noMicrophones: 'No microphones found',
  unnamedMicrophone: (position: number): string => `Microphone ${position}`,
  checkingMicrophone: 'Checking your microphone…',
  cancel: 'Cancel',
  joiningTitle: (ownerName: string | null): string => (ownerName ? `Joining ${ownerName}'s call…` : 'Joining the call…'),
  joiningDetail: 'Connecting your microphone',
  leaveCall: 'Leave call',
  callEnded: 'This call has ended.',
  youStartedThisCall: 'You started this call.',
  micJoinForbidden: "You can't join this call with your mic.",
  micAccessNeeded: 'Microphone access is needed to join with your mic.',
  /** Any other failure: membership unknown (503), the network, a bad answer. */
  joinFailed: "Couldn't join this call. Try again.",
} as const;

/** The watcher's (observer's) choice, controls and header label; also the reconnect overlay's Leave. */
export const WATCHER_COPY = {
  joinAsObserver: 'Join as Observer',
  observerExplainer:
    'Watch the call live without a microphone: the transcript and every alert, view only. Nothing you say is captured.',
  /** Row 1 of the live session header, in place of the owner's "Live". */
  headerLabel: 'Live · Observer',
  leave: 'Leave',
  checklistViewOnly: 'Only the person running this call can check items',
} as const;
